import type { Location } from "@/types";

export class GeolocationService {
  private currentPosition: Location | null = null;
  private watchId: number | null = null;

  async requestLocation(): Promise<Location> {
    return new Promise((resolve, reject) => {
      if (!this.isSupported()) {
        reject(new Error("Геолокация не поддерживается"));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000,
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.currentPosition = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy || 100,
          };
          resolve(this.currentPosition);
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              reject(new Error("Доступ к геолокации запрещён"));
              break;
            case error.POSITION_UNAVAILABLE:
              reject(new Error("Геолокация недоступна"));
              break;
            case error.TIMEOUT:
              reject(new Error("Таймаут запроса геолокации"));
              break;
            default:
              reject(new Error("Неизвестная ошибка геолокации"));
          }
        },
        options,
      );
    });
  }

  startWatching(callback: (location: Location) => void): void {
    if (!this.isSupported()) {
      console.warn("Геолокация не поддерживается");
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    };

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.currentPosition = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy || 100,
        };
        callback(this.currentPosition);
      },
      (error) => {
        console.error("Геолокация ошибка:", error);
      },
      options,
    );
  }

  stopWatching(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  getCurrentLocation(): Location | null {
    return this.currentPosition;
  }

  isSupported(): boolean {
    return "geolocation" in navigator;
  }

  async requestPermission(): Promise<boolean> {
    try {
      await this.requestLocation();
      return true;
    } catch (error) {
      console.error("Ошибка запроса разрешения:", error);
      return false;
    }
  }

  calculateDistance(loc1: Location, loc2: Location): number {
    const R = 6371e3;
    const φ1 = (loc1.latitude * Math.PI) / 180;
    const φ2 = (loc2.latitude * Math.PI) / 180;
    const Δφ = ((loc2.latitude - loc1.latitude) * Math.PI) / 180;
    const Δλ = ((loc2.longitude - loc1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }
}
